import { coral, green } from '@zocdoc/branding/colors';
import { createTimesGridStores as createAPStores } from '@zocdoc/components/src/AppointmentPicker';
import { Breakpoints } from '@zocdoc/components/src/grid';
import { get, isEmpty, isEqual, noop, pick } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { withTheme } from 'styled-components';

import withLocation from '../../../../config/location/withLocation';
import { MARKETING_QUERY_PARAMS, TIMESGRID_QUERY_PARAMS } from '../../../../constants';
import addConstraintModals from '../../../../Shared/Modals/addConstraintModals';
import { isProduction } from '../../../../utils/environment';
import { getSpoQueryParamsFromProviderLocationResult } from '../../../utils/spo';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '../../../../EventTrackingConstants.js';
import {
    LOCALIZED_STRINGS,
    PROVIDER_RESULT_TYPES,
    TRACKING_ACTIONS,
    TRACKING_CONSTANTS,
} from './constants';
import BaseDoctorCard from './DoctorCard';
import * as primitives from './primitives';
import SearchResultRow from './SearchResultRow';
import TelehealthResult from './TelehealthResult';
import APContent from './AppointmentPicker/Content';
import APControls from './AppointmentPicker/Controls';
import { MetricsClickable } from '@zocdoc/frontend-metrics';

const DoctorCard = addConstraintModals(BaseDoctorCard);

export class BaseSearchResults extends Component {
    static propTypes = {
        // injected by the screenshot tests, not normally passed
        __skipTimesGridLoadingState: PropTypes.bool,
        bootstrappedExistingPatientTimes: PropTypes.object,
        bootstrappedNewPatientTimes: PropTypes.object,

        availabilityTimeFilter: PropTypes.number,
        doctorLocationIds: PropTypes.array,
        hasErrors: PropTypes.bool,
        insuranceCarrierImage: PropTypes.string,
        isNewPatient: PropTypes.bool.isRequired,
        location: PropTypes.shape({
            hostname: PropTypes.string,
            query: PropTypes.object,
        }),
        onResultMouseout: PropTypes.func,
        onResultMouseover: PropTypes.func,
        pageId: PropTypes.string,
        providers: PropTypes.shape({
            inNetwork: PropTypes.arrayOf(PropTypes.shape(DoctorCard.propTypes)),
            outOfNetwork: PropTypes.arrayOf(PropTypes.shape(DoctorCard.propTypes)),
            nonInsurance: PropTypes.arrayOf(PropTypes.shape(DoctorCard.propTypes)),
            other: PropTypes.arrayOf(PropTypes.shape(DoctorCard.propTypes)),
        }),
        searchProcedureId: PropTypes.string,
        searchRequestId: PropTypes.string,
        searchSpecialtyId: PropTypes.string,
        sendMetric: PropTypes.func,
        shouldRenderNetworkType: PropTypes.bool,
        showInsurancePicker: PropTypes.func,
        telehealthAds: PropTypes.arrayOf(PropTypes.object),
        trackScrolledToSearchResult: PropTypes.func,
    }

    static defaultProps = {
        __skipTimesGridLoadingState: false,
        doctorLocationIds: [],
        location: {},
        sendMetric: noop,
        shouldRenderNetworkType: true,
        telehealthAds: [],
        showInsurancePicker: noop,
    };

    stores = null;

    constructor(props) {
        super(props);

        const {
            availabilityTimeFilter,
            bootstrappedExistingPatientTimes,
            bootstrappedNewPatientTimes,
            doctorLocationIds,
            insuranceCarrierId,
            insurancePlanId,
            isNewPatient,
            referrerType,
            searchProcedureId,
            theme,
            widget,
        } = props;

        this.state = {
            mounted: false,
            renderMobileViews: true,
        };

        this.stores = createAPStores({
            availabilityTimeFilter,
            bootstrappedExistingPatientTimes,
            bootstrappedNewPatientTimes,
            directoryId: theme.directoryId,
            doctorLocationIds,
            insuranceCarrierId,
            insurancePlanId,
            isExistingPatient: isNewPatient === false,
            procedureId: searchProcedureId,
            referrerType,
            widget,
        });
    }

    componentDidMount() {
        this.setState({ mounted: true });
        this.cacheDesiredRenderState();

        window.addEventListener('resize', this.cacheDesiredRenderState, true);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
    }

    componentDidUpdate(prevProps, prevState) {
        const newParameters = {};

        if (!isEqual(this.props.doctorLocationIds, prevProps.doctorLocationIds)) {
            newParameters.doctorLocationIds = this.props.doctorLocationIds;
        }

        if (this.props.insuranceCarrierId !== prevProps.insuranceCarrierId
            || this.props.insurancePlanId !== prevProps.insurancePlanId
        ) {
            newParameters.insuranceCarrier = this.props.insuranceCarrierId;
            newParameters.insurancePlan = this.props.insurancePlanId;
        }

        if (this.props.searchProcedureId !== prevProps.searchProcedureId) {
            newParameters.procedureId = this.props.searchProcedureId;
        }

        if (this.props.availabilityTimeFilter !== prevProps.availabilityTimeFilter) {
            newParameters.availabilityTimeFilter = this.props.availabilityTimeFilter;
        }

        if (!isEmpty(newParameters)) {
            this.stores.externalActions.changeParameters(newParameters);
        }

        if (this.state.renderMobileViews !== prevState.renderMobileViews) {
            this.stores.gridActions.changeGridType(this.state.renderMobileViews);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.cacheDesiredRenderState, true);
    }

    render() {
        const {
            __skipTimesGridLoadingState,
            providers: {
                inNetwork, other, outOfNetwork, nonInsurance,
            },
            telehealthAds,
        } = this.props;
        const type = PROVIDER_RESULT_TYPES;

        // Telehealth ads are in the nonInsurance section because insurance typically doesn't reimburse for video visits
        // We can't put telehealth ads in the nonInsurance array because the data structures are different
        const shouldRenderNonInsurance = (nonInsurance.length || telehealthAds.length);

        return (
            <primitives.SearchResultsList
                data-test="profList"
                ready={this.state.mounted || __skipTimesGridLoadingState}
            >
                {shouldRenderNonInsurance ? this.renderResultSection(nonInsurance, type.NON_INSURANCE) : null}
                {inNetwork.length ? this.renderResultSection(inNetwork, type.IN_NETWORK) : null}
                {outOfNetwork.length ? this.renderResultSection(outOfNetwork, type.OUT_OF_NETWORK) : null}
                {other && other.length ? this.renderResultSection(other, type.OTHER) : null}
            </primitives.SearchResultsList>
        );
    }

    maybeRenderNetworkType(type, insuranceCarrierImage) {
        if (this.props.shouldRenderNetworkType) {
            switch (type) {
                case PROVIDER_RESULT_TYPES.IN_NETWORK:
                    return (
                        <MetricsClickable
                            component="NetworkType"
                            element="InNetworkProviders"
                        >
                            <primitives.SearchResultsNetworkBarNetworkType
                                data-type={PROVIDER_RESULT_TYPES.IN_NETWORK}
                                onClick={this.props.showInsurancePicker}
                            >
                                <primitives.SearchResultsNetworkBarNetworkTypeCheckIcon
                                    color={green}
                                    data-test="search-results-in-network-icon"
                                    height={18}
                                />
                                <FormattedMessage {...LOCALIZED_STRINGS.ProviderInNetwork} />
                                <primitives.SearchResultsNetworkBarInsuranceImage
                                    alt="Insurance logo"
                                    role="presentation"
                                    src={insuranceCarrierImage}
                                />
                            </primitives.SearchResultsNetworkBarNetworkType>
                        </MetricsClickable>
                    );

                case PROVIDER_RESULT_TYPES.OUT_OF_NETWORK:
                    return (
                        <MetricsClickable
                            component="NetworkType"
                            element="OutOfNetworkProviders"
                        >
                            <primitives.SearchResultsNetworkBarNetworkType
                                data-test="search-results-out-of-network-section"
                                data-type={PROVIDER_RESULT_TYPES.OUT_OF_NETWORK}
                                onClick={this.props.showInsurancePicker}
                            >
                                <primitives.SearchResultsNetworkBarNetworkTypeCloseIcon
                                    color={coral}
                                    data-test="search-results-out-of-network-icon"
                                    height={18}
                                />
                                <FormattedMessage {...LOCALIZED_STRINGS.ProviderOutOfNetwork} />
                            </primitives.SearchResultsNetworkBarNetworkType>
                        </MetricsClickable>
                    );

                case PROVIDER_RESULT_TYPES.OTHER:
                    return (
                        <MetricsClickable
                            component="NetworkType"
                            element="OtherProviders"
                        >
                            <primitives.SearchResultsNetworkBarNetworkType
                                data-type={PROVIDER_RESULT_TYPES.OTHER}
                                onClick={this.props.showInsurancePicker}
                            >
                                <FormattedMessage {...LOCALIZED_STRINGS.ProviderOther} />
                            </primitives.SearchResultsNetworkBarNetworkType>
                        </MetricsClickable>
                    );

                case PROVIDER_RESULT_TYPES.NON_INSURANCE:
                    return (
                        <MetricsClickable
                            component="NetworkType"
                            element="NonInsuranceProvider"
                        >
                            <primitives.SearchResultsNetworkBarNetworkType
                                data-type={PROVIDER_RESULT_TYPES.NON_INSURANCE}
                                onClick={this.props.showInsurancePicker}
                            />
                        </MetricsClickable>
                    );
            }
        }
    }

    renderNonBookableMessage(providerResult) {
        const { defaultMessages } = this.props.theme.components.AppointmentPicker;

        if (providerResult.provider.isPreview) {
            return (
                <primitives.SearchResultsPreviewMessage data-test="search-results-provider-preview-message">
                    <FormattedMessage
                        defaultMessage={defaultMessages.PreviewDoctor}
                        id="SearchResults.AppointmentPicker.PreviewDoctor"
                    />
                </primitives.SearchResultsPreviewMessage>
            );
        } else if (providerResult.location.phone) {
            return (
                <primitives.SearchResultsNonBookableLink
                    data-test="search-results-provider-nonbookable-link"
                    href={`tel:${providerResult.location.phone.replace(/\D/g, '')}`}
                >
                    <FormattedMessage
                        defaultMessage={defaultMessages.NoAvailabilityWithCta}
                        id="SearchResults.AppointmentPicker.NoAvailabilityCta"
                        values={{ phoneNumber: providerResult.location.phone }}
                    />
                </primitives.SearchResultsNonBookableLink>
            );
        }

        return null;
    }

    maybeRenderTelehealthAds(resultType) {
        const { telehealthAds } = this.props;

        if (resultType === PROVIDER_RESULT_TYPES.NON_INSURANCE) {
            return telehealthAds.map(ad => (
                <TelehealthResult
                    key={`telehealth-result-${get(ad, ['provider', 'sourceId'])}`}
                    adData={ad}
                    data-location-id={23525}
                    data-test="tel-ad-result"
                    isProduction={true}
                    onScrolledIntoView={this.handleScrollToSearchResult}
                />
            ));
        }

        return null;
    }

    renderResultSection(results, resultType) {
        const {
            doctorLocationIds,
            insuranceCarrierImage,
            location,
            onResultMouseout,
            onResultMouseover,
            pageId,
            searchProcedureId,
            searchRequestId,
            searchSpecialtyId,
            shouldRenderReviewCount,
            theme,
            updateParameters,
        } = this.props;

        const baseAdditionalBookingUrlParams = {
            directoryId: theme.directoryId,
        };

        if (location) {
            Object.assign(baseAdditionalBookingUrlParams, pick(location.query, MARKETING_QUERY_PARAMS));
            Object.assign(baseAdditionalBookingUrlParams, pick(location.query, TIMESGRID_QUERY_PARAMS));
        }

        const PROD = isProduction(location.hostname);
        return (
            <primitives.Section>
                <primitives.SearchResultsNetworkBarContainer>
                    <primitives.SearchResultsNetworkBar data-test="search-network-bar">
                        {this.maybeRenderNetworkType(resultType, insuranceCarrierImage)}
                        <primitives.SearchResultsNetworkBarTimesControls data-test="search-timesgrid-controls">
                            <APControls
                                doctorLocationIds={doctorLocationIds}
                                fluid
                                isMobile={this.state.renderMobileViews}
                                pageId={pageId}
                                searchRequestId={searchRequestId}
                                stores={this.stores}
                            />
                        </primitives.SearchResultsNetworkBarTimesControls>
                    </primitives.SearchResultsNetworkBar>
                </primitives.SearchResultsNetworkBarContainer>

                {this.maybeRenderTelehealthAds(resultType)}

                {results.map((providerResult, idx) => (
                    <SearchResultRow
                        key={providerResult.isSponsoredResult ? `${providerResult.id}-ad` : providerResult.id}
                        data-location-id={providerResult.id}
                        data-test={`search-result-item${providerResult.isSponsoredResult ? '-ad' : ''}`}
                        data-uem-id={`search-result-item-${providerResult.provider.monolithId}_${providerResult.location.monolithId}`}
                        firstSlot={(
                            <DoctorCard
                                distance={providerResult.distance}
                                insuranceImageUrl={insuranceCarrierImage}
                                isProduction={PROD}
                                isSponsoredResult={providerResult.isSponsoredResult}
                                itemProp="member"
                                location={providerResult.location}
                                locationId={providerResult.location.id}
                                provider={providerResult.provider}
                                providerId={providerResult.provider.id}
                                providerResultType={resultType}
                                searchProcedureId={searchProcedureId}
                                searchResultId={providerResult.searchResultId}
                                searchSpecialtyId={searchSpecialtyId}
                                shouldRenderReviewCount={shouldRenderReviewCount}
                                sponsoredData={providerResult.sponsoredData}
                                updateParameters={updateParameters}
                            />
                        )}
                        lastSlot={
                            !providerResult.provider.nonBookable ? (
                                <primitives.SearchTimesGrid data-test="search-result-item-time-grid">
                                    <APContent
                                        {...this.stores}
                                        additionalBookingUrlParams={{
                                            ...baseAdditionalBookingUrlParams,
                                            ...getSpoQueryParamsFromProviderLocationResult(providerResult),
                                        }}
                                        directoryId={theme.directoryId}
                                        doctorLocationId={`${providerResult.provider.monolithId}_${providerResult.location.monolithId}`}
                                        fluid
                                        index={idx}
                                        isMobile={this.state.renderMobileViews}
                                        loading={!this.state.mounted || !this.props.__skipTimesGridLoadingState}
                                        location={providerResult.location}
                                        numRowsOnLoad={4}
                                        onClickNextAvailable={this.handleNextAvailabilityClicked}
                                        onDataLoaded={this.handleTimesgridContentLoaded}
                                        onTimeSlotClicked={this.handleTimeslotClicked}
                                        phoneNumber={providerResult.location.phone}
                                        profileUrl={providerResult.provider.profileUrl}
                                        provider={providerResult.provider}
                                        searchProcedureId={searchProcedureId}
                                        searchResultId={providerResult.searchResultId}
                                        searchSpecialtyId={searchSpecialtyId}
                                    />
                                </primitives.SearchTimesGrid>
                            ) : (
                                <primitives.SearchResultsNonBookableContainer data-test="search-result-item-nonbookable-info">
                                    {this.renderNonBookableMessage(providerResult)}
                                </primitives.SearchResultsNonBookableContainer>
                            )
                        }
                        onMouseOut={onResultMouseout}
                        onMouseOver={onResultMouseover}
                        onScrolledIntoView={this.handleScrollToSearchResult}
                        searchResultId={providerResult.searchResultId}
                    />
                ))}
            </primitives.Section>
        );
    }

    cacheDesiredRenderState = () => {
        this.setState({
            renderMobileViews: window.innerWidth < Breakpoints.medium,
        });
    }

    handleTimesgridContentLoaded = ({ availability }) => {
        // TODO: remove once internal timesgrid logging is set up
        this.props.sendMetric({
            action: 'Timesgrid Loaded Data',
            component: 'Timesgrid',
            element: 'Timesgrid',
            initiator: EVENT_INITIATORS.SYSTEM,
            interactionType: INTERACTION_TYPES.UPDATE,
            label: availability,
        });
    }

    handleScrollToSearchResult = searchResultId => {
        // Scroll listeners tracking which search results a user scrolls into view
        this.handleSearchResultScrolledIntoView(searchResultId);
    }

    handleSearchResultScrolledIntoView = searchResultId => {
        this.props.trackScrolledToSearchResult(searchResultId);
    }

    handleTimeslotClicked = (_, __, searchResultId) => {
        this.sendProviderLocationCardClickMetric(TRACKING_CONSTANTS.Timeslot, searchResultId);
    }

    handleNextAvailabilityClicked = (_, searchResultId) => {
        this.sendProviderLocationCardClickMetric(TRACKING_CONSTANTS.NextAvailability, searchResultId);
    }

    sendProviderLocationCardClickMetric = (element, searchResultId) => {
        this.props.sendMetric({
            action: TRACKING_ACTIONS.ClickProviderLocationCard,
            component: TRACKING_CONSTANTS.ProviderLocationCard,
            element,
            eventDetails: {
                searchResultId,
            },
        });
    }
}

export default (
    withLocation(
        withTheme(
            BaseSearchResults
        )
    )
);
